<template>
  <!-- Power controls for the persistent server header, so they're on every page.
       Offers only what the current state allows (running → Reboot / Power off,
       stopped → Power on) and locks while a request is in flight or the server
       hasn't reached the new state yet (`pending`, tracked by KvmServiceView). -->
  <div class="d-inline-flex">
    <v-menu offset-y left :disabled="locked">
      <template #activator="{ on, attrs }">
        <gg-btn secondary class="kvm-power-menu__btn"
                :loading="!!pending" :disabled="locked"
                aria-label="Power options" v-bind="attrs" v-on="on">
          <v-icon left small aria-hidden="true">mdi-power</v-icon>Power
          <v-icon right small aria-hidden="true">mdi-menu-down</v-icon>
        </gg-btn>
      </template>
      <v-list dense>
        <v-list-item v-for="opt in options" :key="opt.action" @click="choose(opt)">
          <v-list-item-icon class="mr-3">
            <v-icon small :color="opt.color" aria-hidden="true">{{ opt.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ opt.label }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Reboot / Power off interrupt the server, so confirm them first. -->
    <v-dialog v-model="confirmDialog" max-width="440">
      <gg-card v-if="confirming" role="dialog" aria-modal="true" aria-labelledby="kvm-power-confirm-title">
        <gg-card-text class="pa-4">
          <div id="kvm-power-confirm-title" class="body-1 font-weight-medium mb-2">{{ confirming.label }}?</div>
          <p class="mb-4">{{ confirming.confirm }}</p>
          <div class="text-right">
            <gg-btn text class="mr-2" @click="confirmDialog = false">Cancel</gg-btn>
            <gg-btn tile :color="confirming.color" @click="submit(confirming)">{{ confirming.label }}</gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>
  </div>
</template>

<script>
const OPTIONS = {
  start: { action: 'start', label: 'Power on', icon: 'mdi-power', color: 'primary' },
  reboot: {
    action: 'reboot',
    label: 'Reboot',
    icon: 'mdi-restart',
    color: 'primary',
    confirm: 'The server restarts gracefully. Connections and anything running will be interrupted for a minute or two.',
  },
  stop: {
    action: 'stop',
    label: 'Power off',
    icon: 'mdi-power-off',
    color: 'error',
    confirm: 'The server is stopped immediately, like pulling the plug — unsaved data in running programs may be lost. It stays off until you power it on.',
  },
}

export default {
  name: 'KvmPowerMenu',
  props: {
    server: { type: Object, default: () => ({}) },
    // { action } while a power action is in flight / settling; null when idle.
    pending: { type: Object, default: null },
  },
  data: () => ({
    confirmDialog: false,
    confirming: null,
  }),
  computed: {
    locked () { return !!this.pending || !this.server.power_state },
    options () {
      return this.server.power_state === 'online'
        ? [OPTIONS.reboot, OPTIONS.stop]
        : [OPTIONS.start]
    },
  },
  methods: {
    choose (opt) {
      if (this.locked) return
      if (opt.confirm) {
        this.confirming = opt
        this.confirmDialog = true
      } else {
        this.submit(opt)
      }
    },
    submit (opt) {
      this.confirmDialog = false
      this.$emit('action', opt.action)
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-power-menu__btn:focus-visible {
  @include kvm-focus-ring;
}
</style>
